import { useId, useMemo, useState } from "react";
import {
  Badge,
  Button,
  DataTable,
  Filter,
  Icon,
  InputText,
  RigoFilters,
  ScreenState,
  Tooltip,
  type ColumnDef,
} from "@/deps/ui-library";
import { useChart } from "@/api/queries";
import type { Account } from "@/api/types";
import { useCan } from "@/app/session";
import {
  CLASS_ORDER,
  REQUIRED_TIP,
  SUGGESTED_LOCKED,
  statementLineName,
  subtypeLabel,
} from "@/lib/chart";
import { formatAmount, toNumber } from "@/lib/money";
import { EditAccountDialog, NewAccountDialog } from "../chart/AccountDialogs";
import { RemoveAccountDialog } from "../chart/RemoveAccountDialog";
import { TemplatePicker } from "../chart/TemplatePicker";

const CLASSES = CLASS_ORDER.map((group) => ({ value: group, label: group }));

type Dialog =
  | { kind: "new" }
  | { kind: "edit"; account: Account }
  | { kind: "remove"; account: Account }
  | { kind: "suggested" }
  | null;

function columns(
  onEdit: ((account: Account) => void) | null,
  onRemove: ((account: Account) => void) | null,
): Array<ColumnDef<Account>> {
  const list: Array<ColumnDef<Account>> = [
    {
      id: "code",
      header: "Code",
      size: 72,
      meta: { rowHeader: true },
      cell: ({ row }) => <span className="text-primary font-medium">{row.original.code}</span>,
    },
    {
      id: "name",
      header: "Account",
      cell: ({ row: { original } }) => (
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className={original.isActive ? "text-foreground" : "text-muted-foreground"}>
            {original.name}
          </span>
          {original.isRequired ? (
            <Tooltip content={`${REQUIRED_TIP} ${original.requiredReason ?? ""}`.trim()}>
              <Badge colorScheme="info" size="sm">
                Required
              </Badge>
            </Tooltip>
          ) : null}
          {original.isActive ? null : (
            <Badge colorScheme="default" size="sm">
              Inactive
            </Badge>
          )}
          {original.statementLine ? null : (
            <Badge colorScheme="warning" size="sm">
              Not in layout
            </Badge>
          )}
        </span>
      ),
    },
    {
      id: "line",
      header: "Statement line",
      size: 220,
      cell: ({ row: { original } }) => {
        const type = subtypeLabel(original.subtype);
        const line = (
          // With no line, the name's "Not in layout" badge says so; the column stays empty.
          <span className="text-muted-foreground">
            {original.statementLine ? statementLineName(original.statementLine) : "—"}
          </span>
        );
        return type ? <Tooltip content={type}>{line}</Tooltip> : line;
      },
    },
    {
      id: "balance",
      header: "Balance",
      size: 140,
      meta: { numeric: true },
      cell: ({ row: { original } }) => {
        const value = toNumber(original.balance);
        return (
          <span
            className={
              value < 0
                ? "text-destructive-text"
                : value === 0
                  ? "text-muted-foreground"
                  : "text-foreground"
            }
          >
            {formatAmount(original.balance ?? "0")}
          </span>
        );
      },
    },
  ];
  if (!onEdit || !onRemove) return list;
  list.push({
    id: "actions",
    header: "",
    size: 90,
    cell: ({ row: { original } }) =>
      original.isRequired ? (
        <span className="flex justify-end pr-1.5">
          <Tooltip content={`${REQUIRED_TIP} ${original.requiredReason ?? ""}`.trim()}>
            <span
              role="img"
              aria-label={`${original.code} is a required account`}
              className="text-muted-foreground inline-flex"
            >
              <Icon name="Lock" size="sm" />
            </span>
          </Tooltip>
        </span>
      ) : (
        <span className="flex justify-end gap-1">
          <Tooltip content="Edit">
            <Button.IconButton
              icon="Pencil"
              iconVariant="cell"
              variant="secondary"
              size="xs"
              aria-label={`Edit ${original.code} ${original.name}`}
              onClick={() => onEdit(original)}
            />
          </Tooltip>
          <Tooltip content="Remove">
            <Button.IconButton
              icon="Trash2"
              iconVariant="cell"
              variant="secondary"
              size="xs"
              aria-label={`Remove ${original.code} ${original.name}`}
              onClick={() => onRemove(original)}
            />
          </Tooltip>
        </span>
      ),
  });
  return list;
}

/**
 * Settings › Chart of accounts (handoff §6, ADR-0066): every account by class with its statement line and
 * balance, the required ones locked. New account adds one; Add suggested accounts applies NFRS or IFRS for
 * SMEs, and only before the first transaction posts.
 */
export function ChartTab() {
  const chart = useChart();
  const can = useCan();
  const editable = can("accounting.coa.manage");
  const [dialog, setDialog] = useState<Dialog>(null);
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState<string | undefined>();
  const lockedId = useId();

  const accounts = useMemo(() => chart.data?.accounts ?? [], [chart.data]);
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return accounts
      .filter((a) => !group || a.group === group)
      .filter((a) => !needle || a.code.includes(needle) || a.name.toLowerCase().includes(needle))
      .toSorted(
        (a, b) =>
          CLASS_ORDER.indexOf(a.group) - CLASS_ORDER.indexOf(b.group) ||
          a.code.localeCompare(b.code),
      );
  }, [accounts, group, search]);
  const filtered = !!group || search.trim() !== "";
  const ledgerHasPostings = chart.data?.ledgerHasPostings ?? true;

  const shown = useMemo(
    () =>
      columns(
        editable ? (account) => setDialog({ kind: "edit", account }) : null,
        editable ? (account) => setDialog({ kind: "remove", account }) : null,
      ),
    [editable],
  );

  return (
    <div className="flex flex-col gap-4">
      <RigoFilters.Root>
        <RigoFilters.StaticRow>
          <div className="w-60">
            <InputText.FilterSearch
              aria-label="Search accounts"
              placeholder="Search code or name"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onClear={() => setSearch("")}
            />
          </div>
          <Filter.SingleSelect
            items={CLASSES}
            placeholder="Class"
            searchable={false}
            icon={<Icon name="Funnel" />}
            value={CLASSES.find((option) => option.value === group) ?? null}
            onValueChange={(item) => setGroup(item ? String(item.value) : undefined)}
          />
          {editable ? (
            <span className="ml-auto flex flex-wrap items-center gap-2">
              {ledgerHasPostings ? (
                <>
                  <Tooltip content={SUGGESTED_LOCKED} placement="bottom">
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon="ListPlus"
                      aria-disabled="true"
                      aria-describedby={lockedId}
                      // Unavailable, not inert: it keeps focus so the reason can be reached.
                      onClick={(event) => event.preventDefault()}
                      className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
                    >
                      Add suggested accounts
                    </Button>
                  </Tooltip>
                  <span id={lockedId} className="sr-only">
                    {SUGGESTED_LOCKED}
                  </span>
                </>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon="ListPlus"
                  disabled={!chart.data}
                  onClick={() => setDialog({ kind: "suggested" })}
                >
                  Add suggested accounts
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                leftIcon="Plus"
                disabled={!chart.data}
                onClick={() => setDialog({ kind: "new" })}
              >
                New account
              </Button>
            </span>
          ) : null}
        </RigoFilters.StaticRow>
      </RigoFilters.Root>

      <ScreenState
        loading={chart.isPending}
        fetched={chart.isFetched}
        error={chart.error}
        onRetry={() => void chart.refetch()}
        empty={rows.length === 0}
        filtered={filtered}
        onClearFilters={() => {
          setSearch("");
          setGroup(undefined);
        }}
        errorTitle="The chart of accounts couldn't be loaded"
        emptyState={{
          title: "No accounts yet",
          description: "Add suggested accounts, or add your own with New account.",
        }}
      >
        <DataTable
          aria-label="Chart of accounts"
          size="sm"
          enableSorting={false}
          columns={shown}
          data={rows}
          getRowId={(row) => row.id}
          pagination={{ mode: "manual", show: false }}
        />
      </ScreenState>

      {dialog?.kind === "new" ? (
        <NewAccountDialog chart={accounts} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === "edit" ? (
        <EditAccountDialog account={dialog.account} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === "remove" ? (
        <RemoveAccountDialog account={dialog.account} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === "suggested" ? (
        <TemplatePicker open mode="settings" onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}
